import * as DocumentPicker from 'expo-document-picker';
import * as FileSystem from 'expo-file-system/legacy';
import * as Sharing from 'expo-sharing';
import { Platform } from 'react-native';
import * as XLSX from 'xlsx';

import { sheetCells } from '@/utils/sheet-cells';

export type ExportColumnFormat = 'money' | 'date' | 'integer' | 'text';

export interface ExportColumn<T> {
	header: string;
	/** Pull a string / number / date out of a row for this column. */
	value: (row: T) => unknown;
	/**
	 * Cell value category — drives xlsx numFmt selection. Ignored by CSV.
	 *
	 *   - 'money'   : integer cents → "$1,234.56" with parens-for-negatives
	 *   - 'date'    : ms-since-epoch → m/d/yyyy
	 *   - 'integer' : right-aligned, thousands grouping, no decimals
	 *   - 'text'    : default
	 */
	format?: ExportColumnFormat;
	/**
	 * Approximate column width in pixels. Used by xlsx (chars ≈ px / 7).
	 * Ignored by CSV.
	 */
	width?: number;
}

/**
 * Export a set of rows as CSV. On web the file downloads via an anchor; on
 * native we write to a temp file and hand off to expo-sharing (AirDrop, save
 * to Files, email, etc).
 */
export async function exportRowsToCsv<T>(
	filename: string,
	columns: ExportColumn<T>[],
	rows: T[],
): Promise<void> {
	const header = columns.map((c) => c.header);
	const body = rows.map((r) => columns.map((c) => formatCell(c.value(r))));
	const ws = XLSX.utils.aoa_to_sheet([header, ...body]);
	const csv: string = XLSX.utils.sheet_to_csv(ws);
	await downloadCsvText(filename, csv);
}

/**
 * Hand a CSV string to the user: web downloads via an anchor; native writes a
 * temp file and opens the share sheet (AirDrop, save to Files, email, etc).
 */
export async function downloadCsvText(filename: string, csv: string): Promise<void> {
	const safeName = filename.endsWith('.csv') ? filename : `${filename}.csv`;

	if (Platform.OS === 'web') {
		const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
		const url = URL.createObjectURL(blob);
		const a = document.createElement('a');
		a.href = url;
		a.download = safeName;
		document.body.appendChild(a);
		a.click();
		document.body.removeChild(a);
		URL.revokeObjectURL(url);
		return;
	}

	const uri = `${FileSystem.cacheDirectory}${safeName}`;
	await FileSystem.writeAsStringAsync(uri, csv, { encoding: FileSystem.EncodingType.UTF8 });
	if (await Sharing.isAvailableAsync()) {
		await Sharing.shareAsync(uri, { mimeType: 'text/csv', dialogTitle: safeName, UTI: 'public.comma-separated-values-text' });
	}
}

/**
 * Export a set of rows as a real `.xlsx` workbook. Money cells render with
 * accounting-style currency formatting (parens for negatives); dates render
 * as `m/d/yyyy`; integers get thousands grouping. The header row is frozen so
 * scrolling in Excel/Numbers keeps the column titles visible.
 *
 * Web downloads via `XLSX.writeFile` (anchor-tag plumbing handled inside the
 * lib). Native writes a base64 file to the cache directory and hands off to
 * expo-sharing (AirDrop, Save to Files, email, …).
 */
export async function exportRowsToXlsx<T>(
	filename: string,
	columns: ExportColumn<T>[],
	rows: T[],
): Promise<void> {
	const safeName = filename.endsWith('.xlsx') ? filename : `${filename}.xlsx`;
	const header = columns.map((c) => c.header);
	const body = rows.map((row) => columns.map((col) => formatXlsxCell(col.value(row), col.format)));

	const ws = XLSX.utils.aoa_to_sheet([header, ...body]);

	// Apply per-column numFmt (and date cell-type) to body rows. Skip row 0 (header).
	for (let c = 0; c < columns.length; c++) {
		const col = columns[c];
		const fmt = numFmtFor(col.format);
		const isDate = col.format === 'date';
		if (!fmt && !isDate) continue;
		for (let r = 1; r <= body.length; r++) {
			const addr = XLSX.utils.encode_cell({ r, c });
			const cell = ws[addr];
			if (!cell) continue;
			if (fmt) cell.z = fmt;
			if (isDate && cell.v instanceof Date) cell.t = 'd';
		}
	}

	// Column widths in characters (Excel's approximation: px / 7, min 8).
	ws['!cols'] = columns.map((col) => ({
		wch: col.width ? Math.max(8, Math.round(col.width / 7)) : 14,
	}));

	// Freeze the header row so it stays visible while scrolling the body.
	ws['!views'] = [{ state: 'frozen', xSplit: 0, ySplit: 1, topLeftCell: 'A2' }];

	const wb = XLSX.utils.book_new();
	XLSX.utils.book_append_sheet(wb, ws, 'Sheet1');

	if (Platform.OS === 'web') {
		XLSX.writeFile(wb, safeName);
		return;
	}

	const b64: string = XLSX.write(wb, { type: 'base64', bookType: 'xlsx' });
	const uri = `${FileSystem.cacheDirectory}${safeName}`;
	await FileSystem.writeAsStringAsync(uri, b64, { encoding: FileSystem.EncodingType.Base64 });
	if (await Sharing.isAvailableAsync()) {
		await Sharing.shareAsync(uri, {
			mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
			dialogTitle: safeName,
			UTI: 'org.openxmlformats.spreadsheetml.sheet',
		});
	}
}

function formatXlsxCell(v: unknown, format: ExportColumnFormat | undefined): unknown {
	if (v == null || v === '') return '';
	// Money cells arrive as integer cents — convert to dollars for the numFmt.
	if (format === 'money' && typeof v === 'number') return v / 100;
	// Dates arrive as ms-since-epoch — convert to a real JS Date so xlsx
	// can serialize it as a true date cell (cell.t = 'd').
	if (format === 'date' && typeof v === 'number') return new Date(v);
	if (v instanceof Date) return v;
	if (typeof v === 'number' || typeof v === 'boolean') return v;
	if (typeof v === 'string') return sanitizeCsv(v);
	return sanitizeCsv(JSON.stringify(v));
}

function numFmtFor(format: ExportColumnFormat | undefined): string | null {
	if (format === 'money') return '"$"#,##0.00;("$"#,##0.00)';
	if (format === 'date') return 'm/d/yyyy';
	if (format === 'integer') return '#,##0';
	return null;
}

/**
 * Open a file picker for CSV / Excel, parse the first sheet, and return
 * rows as plain objects keyed by the first-row headers.
 */
export async function pickAndParseRows(): Promise<Record<string, any>[]> {
	const res = await DocumentPicker.getDocumentAsync({
		type: [
			'text/csv',
			'application/vnd.ms-excel',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		],
		copyToCacheDirectory: true,
		multiple: false,
	});
	if (res.canceled || !res.assets?.length) return [];
	const file = res.assets[0];
	const bin = await readAsBinary(file.uri);
	const wb = XLSX.read(bin, { type: 'binary' });
	const firstSheet = wb.Sheets[wb.SheetNames[0]];
	// Header row auto-detected from the first row. Cell values coerced to
	// string by default for predictable downstream handling.
	const json = XLSX.utils.sheet_to_json<Record<string, any>>(firstSheet, { raw: false, defval: '' });
	return json;
}

export interface PickedSpreadsheet {
	/** Original filename as picked. */
	name: string;
	/** CSV text — raw file contents for .csv, sheet_to_csv for binary workbooks. */
	text: string;
	/** First sheet parsed to objects keyed by the header row (cells coerced to string). */
	rows: Record<string, any>[];
	/**
	 * First sheet as a raw cell matrix, values un-coerced.
	 *
	 * `rows` assumes the header is on row 1. Report exports (a utility bill
	 * register, a QuickBooks report) put a title block above theirs, which makes
	 * `rows` useless for them — they need to find the header by content, and
	 * they need real numbers rather than locale-formatted strings.
	 */
	aoa: unknown[][];
	/**
	 * First sheet as a padded string grid (see `sheetCells`): `aoa`'s layout, but a percent
	 * cell reads `1.366%` from its STORED value rather than its rounded display text.
	 */
	cells: string[][];
	/**
	 * The picked file's own uri, so a caller can re-read the ORIGINAL bytes.
	 *
	 * Kept as a uri rather than an ArrayBuffer on purpose: every importer calls
	 * this, and most only ever want the parsed forms above. Holding a second full
	 * copy of every picked workbook in memory to serve the one caller that
	 * archives its source file would be a cost paid by all of them. Read it with
	 * `readPickedFileBytes` at the moment it is needed.
	 */
	uri: string;
}

/**
 * Open a file picker allowing MULTIPLE CSV / Excel files and parse each one.
 * Returns both the row objects (for header-keyed template files) and the raw
 * CSV text (so callers can fall back to bespoke parsers, e.g. the QuickBooks
 * Customer Balance Detail layout whose headers don't sit on the first row).
 */
export async function pickAndParseFiles(): Promise<PickedSpreadsheet[]> {
	const res = await DocumentPicker.getDocumentAsync({
		// Listing extensions alongside MIME types keeps files without a
		// registered MIME selectable on macOS (never mix in '*/*').
		type: [
			'.csv',
			'text/csv',
			'application/vnd.ms-excel',
			'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
		],
		copyToCacheDirectory: true,
		multiple: true,
	});
	if (res.canceled || !res.assets?.length) return [];

	const out: PickedSpreadsheet[] = [];
	for (const file of res.assets) {
		// CSVs are read as UTF-8 text (a binary-string read would mangle
		// multibyte characters); only real workbooks need the binary path.
		const isCsv = /\.(csv|txt)$/i.test(file.name ?? '');
		const wb = isCsv
			? XLSX.read(await readAsText(file.uri), { type: 'string' })
			: XLSX.read(await readAsBinary(file.uri), { type: 'binary' });
		const firstSheet = wb.Sheets[wb.SheetNames[0]];
		const rows = XLSX.utils.sheet_to_json<Record<string, any>>(firstSheet, { raw: false, defval: '' });
		const text = XLSX.utils.sheet_to_csv(firstSheet);
		// raw: true so money cells stay numbers — a formatted "1,234.56" would
		// have to be un-formatted again, and locale grouping varies by exporter.
		const aoa = XLSX.utils.sheet_to_json<unknown[]>(firstSheet, { header: 1, blankrows: false, raw: true });
		out.push({ name: file.name ?? 'file.csv', text, rows, aoa, cells: sheetCells(firstSheet), uri: file.uri });
	}
	return out;
}

async function readAsText(uri: string): Promise<string> {
	if (Platform.OS === 'web') {
		const resp = await fetch(uri);
		return resp.text();
	}
	return FileSystem.readAsStringAsync(uri, { encoding: FileSystem.EncodingType.UTF8 });
}

/**
 * A picked file's ORIGINAL bytes, for callers that archive the source document
 * rather than only its parsed contents.
 *
 * Must not be reconstructed from `text`/`aoa` — those are lossy views (a
 * workbook round-tripped through `sheet_to_csv` loses every sheet but the
 * first, all formatting, and the exact bytes an auditor would want to compare).
 */
export async function readPickedFileBytes(uri: string): Promise<ArrayBuffer> {
	if (Platform.OS === 'web') {
		const resp = await fetch(uri);
		return await resp.arrayBuffer();
	}
	const b64 = await FileSystem.readAsStringAsync(uri, { encoding: FileSystem.EncodingType.Base64 });
	const binary = atob(b64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
	return bytes.buffer;
}

async function readAsBinary(uri: string): Promise<string> {
	if (Platform.OS === 'web') {
		// On web the uri is an object URL; fetch it to get the bytes.
		const resp = await fetch(uri);
		const buf = await resp.arrayBuffer();
		return arrayBufferToBinaryString(buf);
	}
	const b64 = await FileSystem.readAsStringAsync(uri, { encoding: FileSystem.EncodingType.Base64 });
	return atob(b64);
}

function arrayBufferToBinaryString(buffer: ArrayBuffer): string {
	const bytes = new Uint8Array(buffer);
	let out = '';
	for (let i = 0; i < bytes.byteLength; i++) out += String.fromCharCode(bytes[i]);
	return out;
}

// CSV formula injection: Excel/Sheets evaluate cells starting with =, +, -, @,
// or a leading tab/CR as formulas. Prefix a tab so the cell renders as text.
const sanitizeCsv = (s: string): string => (/^[=+\-@\t\r]/.test(s) ? `\t${s}` : s);

function formatCell(v: unknown): string | number {
	if (v == null) return '';
	if (v instanceof Date) return v.toISOString();
	if (typeof v === 'number') return v;
	if (typeof v === 'string') return sanitizeCsv(v);
	if (typeof v === 'boolean') return v ? 'true' : 'false';
	return sanitizeCsv(JSON.stringify(v));
}

/** Convert integer cents to "12.34" for CSV export. */
export const centsToDollars = (cents: number | null | undefined): string => {
	if (cents == null) return '';
	return (Number(cents) / 100).toFixed(2);
};

/** Parse a dollar amount string from a CSV back into integer cents. */
export const dollarsToCents = (s: unknown): number | null => {
	if (s == null || s === '') return null;
	const raw = String(s).trim();
	// Accounting-style negatives: "(125.00)" means -125.00. Stripping the
	// parens without flipping the sign would import prepaid balances as owed.
	const negative = /^\(.*\)$/.test(raw);
	const cleaned = raw.replace(/[^0-9.-]/g, '');
	// No digits at all ("n/a", "--") is unreadable, not zero.
	if (!/[0-9]/.test(cleaned)) return null;
	const n = Number(cleaned);
	if (!isFinite(n)) return null;
	const cents = Math.round(n * 100);
	return negative ? -Math.abs(cents) : cents;
};
